// src/store/chatStore.ts
import { create, StoreApi, UseBoundStore } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";
import { apiService, Room, Message } from "@/services/apiService";

interface ChatState {
  // State
  rooms: Room[];
  selectedRoomId: number | null;
  selectedRoomSlug: string | null;
  messages: Record<string, Message[]>;
  searchQuery: string;
  isLoading: boolean;
  error: Error | null;
  currentUserId: number | null;
  currentUsername: string |null;
  isSendingMessage: boolean;

  // Actions
  setCurrentUserId: (currentUserId: number) => void;
  setCurrentUsername: (currentUsername: string) => void;
  setRooms: (rooms: Room[]) => void;
  setSelectedRoom: (roomId: number | null) => void;
  setSelectedRoomSlug: (roomSlug: string | null) => void;
  setMessages: (roomId: number, messages: Message[]) => void;
  addMessage: (roomId: number, message: Message) => void;
  setSearchQuery: (query: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  clearMessages: (roomId: number) => void;
  setSendingMessage:(isSendingMessage:boolean) => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      rooms: [],
      selectedRoomId: null,
      selectedRoomSlug:null,
      messages: {},
      searchQuery: "",
      isLoading: false,
      error: null,
      currentUserId:null,
      currentUsername:null,
      isSendingMessage:false,

      // Actions
      setCurrentUserId: (currentUserId) => set({ currentUserId }),
      setCurrentUsername: (currentUsername) => set({ currentUsername }),

      setRooms: (rooms) => set({ rooms }),

      setSelectedRoom: (roomId) => set({ selectedRoomId: roomId }),

      setSelectedRoomSlug:(roomSlug) => set ({selectedRoomSlug: roomSlug}),

      setMessages: (roomId, messages) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [roomId]: messages,
          },
        })),

      addMessage: (roomId, message) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [roomId]: [...(state.messages[roomId] || []), message],
          },
        })),

      setSearchQuery: (query) => set({ searchQuery: query }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      clearMessages: (roomId) =>
        set((state) => ({
          messages: {
            ...state.messages,
            [roomId]: [],
          },
        })),
        setSendingMessage: (isSending) => set({ isSendingMessage: isSending }),
    }),
    {
      name: "chat-storage",
      partialize: (state) => ({
        searchQuery: state.searchQuery,
        currentUserId:state.currentUserId,
      }),
    }
  )
);
